import { useCallback, useMemo } from 'react';

import { QueryHookOptions, useQuery } from '@apollo/client';
import { DocumentNode } from '@apollo/client';
import { OperationVariables, QueryResult } from '@apollo/client';

// Modify is a utility type that changes the type of an existing field
// We can not directly override a field's type so instead we remove the old field and add a new one
// https://stackoverflow.com/a/55032655
type Modify<T, R> = Omit<T, keyof R> & R;

type WrangledQueryResult<TData, TWrangledData, TVariables extends OperationVariables> = Modify<
  QueryResult<TData, TVariables>,
  {
    data?: TWrangledData;
  }
>;

interface HookOptions<TData, TWrangledData, TVariables extends OperationVariables> {
  wrangler?: (data: TData) => TWrangledData;
  defaultOptions?: QueryHookOptions<TData, TVariables>;
}

/**
 * A high order function that accepts a GraphQL `DocumentNode` AST representing a `Query`
 * and type information and produces a React Hook function to interact with the Query
 * while providing proper typing information without needing to specify at the call site.
 */
export const buildQueryHook =
  <TData, TVariables extends OperationVariables = OperationVariables, TWrangledData = TData>(
    query: DocumentNode,
    options?: HookOptions<TData, TWrangledData, TVariables>,
  ) =>
  (
    queryOptions?: QueryHookOptions<TData, TVariables>,
  ): WrangledQueryResult<TData, TWrangledData, TVariables> => {
    const noopWrangler = useCallback((data: TData) => data as unknown as TWrangledData, []);
    const wrangler = (options && options.wrangler) || noopWrangler;
    const defaultQueryOptions = (options && options.defaultOptions) || {};

    const queryResult = useQuery<TData, TVariables>(query, {
      ...defaultQueryOptions,
      ...queryOptions,
    });

    const data = useMemo(
      () => queryResult.data && wrangler(queryResult.data),
      [queryResult.data, wrangler],
    );

    return {
      ...queryResult,
      data,
    };
  };
